import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { departmentAPI, transactionAPI, ruleAPI, notificationAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Wallet, Send, History, Scale, BookUser, ArrowUpRight,
  CreditCard, Copy, Bell, CheckCircle, XCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const DeptDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [department, setDepartment] = useState(null);
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [deptRes, rulesRes, txnRes, notifRes] = await Promise.all([
        departmentAPI.list(),
        ruleAPI.listAll(),
        transactionAPI.list({ limit: 5 }),
        notificationAPI.list(false)
      ]);
      
      // Department head sees their own department
      const myDept = deptRes.data[0];
      setDepartment(myDept);
      
      if (myDept) {
        const statsRes = await departmentAPI.getStats(myDept.department_id);
        setStats(statsRes.data);
      }
      
      setRules(rulesRes.data);
      setRecentPayments(txnRes.data);
      setNotifications(notifRes.data.slice(0, 5));
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount || 0);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const getStatusStyle = (status) => {
    const styles = {
      'COMPLETED': 'status-success',
      'PENDING_APPROVAL': 'status-pending',
      'REJECTED': 'status-error',
      'PROCESSING': 'status-info',
      'INFO_REQUESTED': 'status-pending'
    };
    return styles[status] || 'status-info';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!department) {
    return (
      <DashboardLayout>
        <Card className="glass-card">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Wallet className="w-16 h-16 text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Department Assigned</h2>
            <p className="text-gray-500">Please contact your administrator to be assigned to a department.</p>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dept-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">{department.name}</h1>
            <p className="text-gray-500 mt-1">Welcome back, {user?.name}</p>
          </div>
          <Link to="/department/payment">
            <Button className="bg-blue-900 hover:bg-blue-800 text-gray-900 gap-2 glow-primary">
              <Send className="w-4 h-4" /> Initiate Payment
            </Button>
          </Link>
        </div>

        {/* Virtual Account Card */}
        {department.virtual_account && (
          <Card className="virtual-account-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <CreditCard className="w-8 h-8 text-blue-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Your Virtual Account</h3>
                  <p className="text-sm text-gray-500">Share these details to receive payments</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-black/20 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Balance</p>
                  <p className="text-2xl font-bold text-blue-600 font-['JetBrains_Mono']">{formatCurrency(department.balance)}</p>
                </div>
                <div className="bg-black/20 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Account Number</p>
                  <div className="flex items-center justify-between">
                    <p className="font-['JetBrains_Mono'] text-gray-900 text-sm">{department.virtual_account.account_number}</p>
                    <button onClick={() => copyToClipboard(department.virtual_account.account_number)} className="text-gray-500 hover:text-gray-900"><Copy className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="bg-black/20 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">UPI ID</p>
                  <div className="flex items-center justify-between">
                    <p className="font-['JetBrains_Mono'] text-gray-900 text-sm">{department.virtual_account.upi_id}</p>
                    <button onClick={() => copyToClipboard(department.virtual_account.upi_id)} className="text-gray-500 hover:text-gray-900"><Copy className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="bg-black/20 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">IFSC Code</p>
                  <p className="font-['JetBrains_Mono'] text-gray-900 text-sm">{department.virtual_account.ifsc_code}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card">
            <CardContent className="p-5">
              <History className="w-5 h-5 text-blue-500 mb-2" />
              <p className="text-xs text-gray-500">Completed</p>
              <p className="text-xl font-bold text-gray-900">{stats?.completed_transactions || 0}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-5">
              <Clock className="w-5 h-5 text-amber-500 mb-2" />
              <p className="text-xs text-gray-500">Pending</p>
              <p className="text-xl font-bold text-gray-900">{stats?.pending_transactions || 0}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-5">
              <ArrowUpRight className="w-5 h-5 text-purple-500 mb-2" />
              <p className="text-xs text-gray-500">Total Spent</p>
              <p className="text-xl font-bold text-gray-900 font-['JetBrains_Mono']">{formatCurrency(stats?.total_spent)}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-5">
              <BookUser className="w-5 h-5 text-pink-500 mb-2" />
              <p className="text-xs text-gray-500">Beneficiaries</p>
              <p className="text-xl font-bold text-gray-900">{stats?.beneficiary_count || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Recent Payments */}
          <Card className="glass-card col-span-12 lg:col-span-7">
            <CardHeader className="border-b border-gray-200 pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <History className="w-5 h-5 text-blue-600" /> Recent Payments
              </CardTitle>
              <Link to="/department/payments">
                <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-500">View All</Button>
              </Link>
            </CardHeader>
            <CardContent className="pt-4">
              {recentPayments.length > 0 ? (
                <div className="space-y-3">
                  {recentPayments.map((txn) => (
                    <div key={txn.transaction_id} className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${txn.status === 'COMPLETED' ? 'bg-blue-900/10' : txn.status === 'REJECTED' ? 'bg-red-500/10' : 'bg-amber-500/10'}`}>
                          {txn.status === 'COMPLETED' ? <CheckCircle className="w-5 h-5 text-blue-600" /> : txn.status === 'REJECTED' ? <XCircle className="w-5 h-5 text-red-500" /> : <Clock className="w-5 h-5 text-amber-500" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{txn.beneficiary_name}</p>
                          <p className="text-xs text-gray-500">{new Date(txn.submitted_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-['JetBrains_Mono'] text-gray-900">{formatCurrency(txn.amount)}</p>
                        <Badge className={getStatusStyle(txn.status)}>{txn.status.replace('_', ' ')}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">No payments yet</div>
              )}
            </CardContent>
          </Card>

          {/* Rules & Limits */}
          <Card className="glass-card col-span-12 lg:col-span-5">
            <CardHeader className="border-b border-gray-200 pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Scale className="w-5 h-5 text-amber-500" /> My Rules & Limits
              </CardTitle>
              <Link to="/department/rules">
                <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-500">View All</Button>
              </Link>
            </CardHeader>
            <CardContent className="pt-4">
              {rules.length > 0 ? (
                <div className="space-y-4">
                  {rules.slice(0, 3).map((rule) => {
                    const utilization = rule.amount > 0 ? (rule.current_usage / rule.amount) * 100 : 0;
                    return (
                      <div key={rule.rule_id} className="p-3 bg-white/50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-gray-900">{rule.rule_type.replace('_', ' ')}</p>
                          <span className="text-xs text-gray-500">{rule.period || 'Total'}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs mb-2">
                          <span className="text-gray-500">{formatCurrency(rule.current_usage)} / {formatCurrency(rule.amount)}</span>
                          <span className="text-blue-600">{formatCurrency(rule.remaining_budget)} remaining</span>
                        </div>
                        <Progress value={Math.min(utilization, 100)} className="h-2" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">No rules configured</div>
              )}
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card className="glass-card col-span-12">
            <CardHeader className="border-b border-gray-200 pb-4">
              <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-500" /> Recent Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {notifications.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {notifications.map((n) => (
                    <div key={n.notification_id} className={`p-4 rounded-lg ${n.is_read ? 'bg-white/30' : 'bg-white/50 border-l-2 border-blue-600'}`}>
                      <p className="text-sm font-medium text-gray-900 mb-1">{n.title}</p>
                      <p className="text-xs text-gray-500 line-clamp-2">{n.message}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">No notifications</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DeptDashboard;
